// ComponentCandidatesAdminPage.js — Phase CW.6 of Catalog Writeback
//
// Admin-only review queue at /admin/component-candidates. Lists
// `component_candidates` docs from Firestore, lets the admin run the
// discriminator (Anthropic/OpenAI LLM call, returns confidence + suggested
// slug/category/keywords), then promote (commits files + indexPatch +
// CONTEXT.md to mchatai-source via GitHub API) or reject (logs reason).
//
// Mirrors TemplateCandidatesAdminPage.js but for catalog Lego components
// (web-components / macos-components / mobile-components / pipelines).
//
// Auth: same UID allowlist + custom-claim pattern as the template page.

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './App.css';

const ADMIN_UIDS = ['GI1GHQ7wpLQKjJyf9QRQATyw5e33', 'vGB6BL3iJfh32aWZ0HA93nUV2013'];

const PLATFORMS = [
  { id: 'all', label: 'All platforms' },
  { id: 'web-components', label: 'Web' },
  { id: 'macos-components', label: 'macOS' },
  { id: 'mobile-components', label: 'Mobile' },
  { id: 'pipelines', label: 'Pipelines' }
];

const STATUS_FILTERS = [
  { id: 'unscreened', label: 'Unscreened' },
  { id: 'recommended', label: 'Recommended (discriminator: promote)' },
  { id: 'declined-by-discriminator', label: 'Declined by discriminator' },
  { id: 'promoted', label: 'Promoted' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' }
];

function isAdmin(user) {
  if (!user) return false;
  if (ADMIN_UIDS.includes(user.uid)) return true;
  return user.idTokenResult?.claims?.role === 'admin';
}

export default function ComponentCandidatesAdminPage() {
  const auth = getAuth();
  const [user, setUser] = useState(auth.currentUser);
  const [authChecked, setAuthChecked] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [platformFilter, setPlatformFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('unscreened');
  const [busy, setBusy] = useState({}); // candidateID → action in flight

  // Auth check
  useEffect(() => {
    return auth.onAuthStateChanged(async (u) => {
      if (u) {
        try {
          const tokenResult = await u.getIdTokenResult();
          setUser({ ...u, idTokenResult: tokenResult });
        } catch {
          setUser(u);
        }
      } else {
        setUser(null);
      }
      setAuthChecked(true);
    });
  }, [auth]);

  // Load candidates
  const loadCandidates = useCallback(async () => {
    if (!user || !isAdmin(user)) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'component_candidates'),
        orderBy('createdAt', 'desc'),
        limit(200)
      );
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCandidates(docs);
    } catch (err) {
      setErrorMsg(`Load failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  // Filtering
  const filtered = candidates.filter(c => {
    if (platformFilter !== 'all' && c.platform !== platformFilter) return false;
    switch (statusFilter) {
      case 'unscreened':
        return !c.discriminator && !c.adminDecision;
      case 'recommended':
        return c.discriminator?.decision === 'promote' && !c.adminDecision;
      case 'declined-by-discriminator':
        return c.discriminator?.decision === 'decline' && !c.adminDecision;
      case 'promoted':
        return c.adminDecision?.kind === 'promoted';
      case 'rejected':
        return c.adminDecision?.kind === 'rejected';
      case 'all':
      default:
        return true;
    }
  });

  // Cloud Function callers
  const callFunction = async (name, payload) => {
    const fns = getFunctions();
    const fn = httpsCallable(fns, name);
    const result = await fn(payload);
    return result.data;
  };

  const onRunDiscriminator = async (cand) => {
    setBusy(prev => ({ ...prev, [cand.id]: 'discriminator' }));
    try {
      await callFunction('runComponentDiscriminator', { candidateID: cand.id });
      await loadCandidates();
    } catch (err) {
      setErrorMsg(`Discriminator failed: ${err.message}`);
    } finally {
      setBusy(prev => { const c = { ...prev }; delete c[cand.id]; return c; });
    }
  };

  const onPromote = async (cand) => {
    const slug = window.prompt(
      'Confirm slug for the promoted component (or leave blank to use suggested):',
      cand.discriminator?.suggestedSlug || ''
    );
    if (slug === null) return; // user cancelled
    setBusy(prev => ({ ...prev, [cand.id]: 'promote' }));
    try {
      const result = await callFunction('promoteComponentCandidate', {
        candidateID: cand.id,
        slug: slug || undefined
      });
      window.alert(`Promoted! Commit: ${result.commitURL || result.commitSha}`);
      await loadCandidates();
    } catch (err) {
      setErrorMsg(`Promote failed: ${err.message}`);
    } finally {
      setBusy(prev => { const c = { ...prev }; delete c[cand.id]; return c; });
    }
  };

  const onRunSandbox = async (cand, kind) => {
    setBusy(prev => ({ ...prev, [cand.id]: `sandbox-${kind}` }));
    try {
      const result = await callFunction('runComponentInSandbox', {
        candidateID: cand.id,
        kind
      });
      if (result.previewURL) {
        window.open(result.previewURL, '_blank');
      }
      await loadCandidates();
    } catch (err) {
      setErrorMsg(`Sandbox run failed: ${err.message}`);
    } finally {
      setBusy(prev => { const c = { ...prev }; delete c[cand.id]; return c; });
    }
  };

  const onReject = async (cand) => {
    const reason = window.prompt('Rejection reason (audit log):');
    if (!reason) return;
    setBusy(prev => ({ ...prev, [cand.id]: 'reject' }));
    try {
      await callFunction('rejectComponentCandidate', {
        candidateID: cand.id,
        reason
      });
      await loadCandidates();
    } catch (err) {
      setErrorMsg(`Reject failed: ${err.message}`);
    } finally {
      setBusy(prev => { const c = { ...prev }; delete c[cand.id]; return c; });
    }
  };

  // Render gates
  if (!authChecked) {
    return <div className="admin-page"><p>Loading…</p></div>;
  }
  if (!user) {
    return (
      <div className="admin-page">
        <h1>Component Candidates</h1>
        <p>Sign in to access this admin page.</p>
        <Link to="/login">Sign in</Link>
      </div>
    );
  }
  if (!isAdmin(user)) {
    return (
      <div className="admin-page">
        <h1>Component Candidates</h1>
        <p>Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="admin-page" style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1>Component Candidates</h1>
        <div>
          <Link to="/admin/template-candidates" style={{ marginRight: 16 }}>← Templates</Link>
          <button onClick={loadCandidates} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      <p style={{ color: '#666', fontSize: 14 }}>
        Lego components extracted from user-deployed apps. Run the discriminator
        for a screening verdict, inspect the bundle + diff against the live
        catalog, then promote to commit it to{' '}
        <code>mchatai-source/frameworks/&lt;platform&gt;/components/</code>.
      </p>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <label>
          Platform:&nbsp;
          <select value={platformFilter} onChange={e => setPlatformFilter(e.target.value)}>
            {PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </label>
        <label>
          Status:&nbsp;
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            {STATUS_FILTERS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
        <span style={{ color: '#666' }}>
          {filtered.length} of {candidates.length} candidates
        </span>
      </div>

      {errorMsg && (
        <div style={{ padding: 12, background: '#fee', border: '1px solid #fbb', borderRadius: 4, marginBottom: 16 }}>
          {errorMsg}
        </div>
      )}

      {/* Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #ccc', textAlign: 'left' }}>
            <th style={{ padding: 8 }}>Component</th>
            <th style={{ padding: 8 }}>Platform</th>
            <th style={{ padding: 8 }}>Trigger</th>
            <th style={{ padding: 8 }}>Discriminator</th>
            <th style={{ padding: 8 }}>Validator</th>
            <th style={{ padding: 8 }}>Status</th>
            <th style={{ padding: 8 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(cand => {
            const isBusy = !!busy[cand.id];
            const status = cand.adminDecision?.kind
              || (cand.discriminator ? `screened: ${cand.discriminator.decision}` : 'unscreened');
            return (
              <tr key={cand.id} style={{ borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                <td style={{ padding: 8, maxWidth: 280 }}>
                  <strong>{cand.componentID || '(no id)'}</strong>
                  <br />
                  <small style={{ color: '#666' }}>{cand.componentName}</small>
                  <br />
                  <small style={{ color: '#666' }}>category: {cand.category}</small>
                  <br />
                  <small style={{ color: '#888' }}>
                    {cand.parentPublishedURL && (
                      <a href={cand.parentPublishedURL} target="_blank" rel="noreferrer">parent app</a>
                    )}
                    {cand.bundleURL && (
                      <> · <a href={cand.bundleURL} target="_blank" rel="noreferrer">bundle.zip</a></>
                    )}
                  </small>
                </td>
                <td style={{ padding: 8 }}>{cand.platform}</td>
                <td style={{ padding: 8 }}>
                  {cand.triggeringSignal}
                  <br />
                  <small style={{ color: '#666' }}>w={cand.triggerSignalWeight}</small>
                </td>
                <td style={{ padding: 8, maxWidth: 240 }}>
                  {cand.discriminator ? (
                    <>
                      <strong style={{ color: cand.discriminator.decision === 'promote' ? '#0a0' : '#a00' }}>
                        {cand.discriminator.decision}
                      </strong>{' '}
                      <small>({(cand.discriminator.confidence * 100).toFixed(0)}%)</small>
                      <br />
                      <small style={{ color: '#666' }}>{cand.discriminator.reasoning?.slice(0, 140)}</small>
                    </>
                  ) : (
                    <small style={{ color: '#999' }}>not yet run</small>
                  )}
                </td>
                <td style={{ padding: 8 }}>
                  <small>
                    {cand.validatorPassedRules?.length || 0}/7 rules
                    <br />
                    {cand.extractorProvider}
                  </small>
                </td>
                <td style={{ padding: 8 }}>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: 12,
                    fontSize: 12,
                    background: cand.adminDecision?.kind === 'promoted' ? '#cfc'
                      : cand.adminDecision?.kind === 'rejected' ? '#fcc'
                      : '#eee'
                  }}>
                    {status}
                  </span>
                  {cand.adminDecision?.commitURL && (
                    <>
                      <br />
                      <small>
                        <a href={cand.adminDecision.commitURL} target="_blank" rel="noreferrer">commit</a>
                      </small>
                    </>
                  )}
                </td>
                <td style={{ padding: 8, whiteSpace: 'nowrap' }}>
                  {!cand.adminDecision && (
                    <>
                      <button onClick={() => onRunDiscriminator(cand)} disabled={isBusy}>
                        {busy[cand.id] === 'discriminator' ? 'Running…' : 'Discriminator'}
                      </button>
                      &nbsp;
                      <button
                        onClick={() => onRunSandbox(cand, cand.platform === 'macos-components' ? 'macOSApp' : cand.platform === 'pipelines' ? 'pipeline' : 'miniApp')}
                        disabled={isBusy}
                        title="Open the bundle.zip preview in a new tab (MVP — full sandbox host coming soon)"
                      >
                        {busy[cand.id]?.startsWith('sandbox') ? 'Opening…' : 'Sandbox'}
                      </button>
                      &nbsp;
                      <button
                        onClick={() => onPromote(cand)}
                        disabled={isBusy || cand.discriminator?.decision === 'decline'}
                        title={cand.discriminator?.decision === 'decline' ? 'Discriminator declined this candidate' : ''}
                      >
                        {busy[cand.id] === 'promote' ? 'Promoting…' : 'Promote'}
                      </button>
                      &nbsp;
                      <button onClick={() => onReject(cand)} disabled={isBusy}>
                        {busy[cand.id] === 'reject' ? 'Rejecting…' : 'Reject'}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {filtered.length === 0 && !loading && (
        <p style={{ padding: 24, textAlign: 'center', color: '#666' }}>
          No candidates match the current filter.
        </p>
      )}
    </div>
  );
}
